<template>
  <main class="quiz-play-page">
    <div class="play-ambient" aria-hidden="true"></div>

    <header class="play-header">
      <button type="button" class="icon-button" aria-label="Quiz verlassen" @click="leaveQuiz">
        <v-icon>mdi-close</v-icon>
      </button>
      <div class="play-brand">
        <img :src="brandLogo" alt="WELL COME FIT" width="1200" height="931" />
        <div><strong>LIVE QUIZ</strong><small>Teilnehmeransicht</small></div>
      </div>
      <div class="header-actions">
        <span class="connection-pill" :class="{ offline: !isConnected }"><i></i>{{ isConnected ? "Live" : "Verbinde" }}</span>
        <button type="button" class="icon-button" :aria-label="soundEnabled ? 'Musik ausschalten' : 'Musik einschalten'" @click="toggleSound">
          <v-icon>{{ soundEnabled ? "mdi-volume-high" : "mdi-volume-off" }}</v-icon>
        </button>
      </div>
    </header>

    <section v-if="joining && !snapshot" class="center-state">
      <span class="loading-orbit"><i></i><v-icon>mdi-head-question-outline</v-icon></span>
      <h1>Du wirst verbunden</h1>
      <p>Die Live-Session wird synchronisiert.</p>
    </section>

    <section v-else-if="!snapshot" class="center-state error-state">
      <span><v-icon>mdi-alert-circle-outline</v-icon></span>
      <h1>Quiz nicht erreichbar</h1>
      <p>{{ error || "Die Live-Session konnte nicht geladen werden." }}</p>
      <div class="state-actions">
        <v-btn outlined color="#58d388" large @click="joinSession">Erneut versuchen</v-btn>
        <v-btn text color="#aab5af" large @click="leaveQuiz">Zur Übersicht</v-btn>
      </div>
    </section>

    <template v-else>
      <div v-if="error" class="live-error" role="alert">
        <v-icon>mdi-alert-circle-outline</v-icon><span>{{ error }}</span>
        <button type="button" aria-label="Meldung schliessen" @click="clearError"><v-icon>mdi-close</v-icon></button>
      </div>

      <section v-if="snapshot.state === 'lobby'" class="stage lobby-stage">
        <span class="stage-kicker"><i></i> DU BIST DABEI</span>
        <h1>{{ snapshot.quizTitle }}</h1>
        <p class="waiting-copy">Der Leiter startet das Quiz, sobald alle bereit sind.</p>

        <div class="player-ticket">
          <div class="avatar-mark">{{ initials }}</div>
          <div><small>Eingeloggt als</small><strong>{{ personalName }}</strong></div>
          <span><v-icon>mdi-check-circle</v-icon> Bereit</span>
        </div>

        <div class="lobby-facts">
          <div><span>{{ connectedParticipantCount }}</span><small>Spieler dabei</small></div>
          <div><span>{{ formatCode(snapshot.joinCode) }}</span><small>Quiz-Code</small></div>
          <div><span>{{ snapshot.totalQuestions }}</span><small>Fragen</small></div>
        </div>

        <div class="participant-cloud" aria-label="Teilnehmer">
          <span v-for="participant in snapshot.participants" :key="participant.participantId" :class="{ offline: !participant.connected }">
            <i>{{ participantInitials(participant.displayName) }}</i>{{ participant.displayName }}
          </span>
        </div>
      </section>

      <section v-else-if="snapshot.state === 'countdown'" class="stage countdown-stage">
        <span class="stage-kicker">FRAGE {{ snapshot.currentQuestionIndex + 1 }} VON {{ snapshot.totalQuestions }}</span>
        <div class="countdown-number" :key="countdownValue">{{ countdownValue }}</div>
        <h1>Mach dich bereit!</h1>
        <p>Die nächste Frage erscheint gleich.</p>
      </section>

      <section v-else-if="snapshot.state === 'question'" class="stage question-stage">
        <div class="question-meta">
          <span>Frage {{ snapshot.currentQuestionIndex + 1 }} / {{ snapshot.totalQuestions }}</span>
          <span class="answer-counter"><v-icon>mdi-account-check-outline</v-icon>{{ snapshot.answerCount }} geantwortet</span>
        </div>
        <div class="timer-track" aria-hidden="true"><i :style="{ width: `${questionProgress}%` }"></i></div>

        <div class="question-heading">
          <h1>{{ snapshot.question.text }}</h1>
          <div class="timer-bubble" :style="{ '--timer-progress': `${questionProgress * 3.6}deg` }">
            <span>{{ remainingSeconds }}</span><small>Sek.</small>
          </div>
        </div>

        <div v-if="isEstimateQuestion" class="estimate-answer-card">
          <span class="estimate-icon"><v-icon>mdi-chart-bell-curve-cumulative</v-icon></span>
          <div class="estimate-copy">
            <strong>Deine Schätzung</strong>
            <small>
              {{ snapshot.question.estimateMode === "closest" ? "Der nächste Wert gewinnt." : `Werte innerhalb ±${formatEstimate(snapshot.question.tolerancePercent)} % erhalten Punkte.` }}
            </small>
          </div>
          <div class="estimate-input-row">
            <v-text-field
              v-model="estimateInput"
              :suffix="snapshot.question.unit || undefined"
              label="Wert eingeben"
              inputmode="decimal"
              outlined
              dark
              hide-details="auto"
              :disabled="answerLocked"
              :error-messages="estimateValidationError ? [estimateValidationError] : []"
              @keyup.enter="submitEstimate" />
            <v-btn color="#58d388" depressed large :loading="answerPending" :disabled="answerLocked || !hasEstimateInput" @click="submitEstimate">
              Schätzung abgeben
            </v-btn>
          </div>
        </div>

        <div v-else class="answer-grid">
          <button
            v-for="(answer, index) in snapshot.question.answers"
            :key="answer._id"
            type="button"
            class="answer-button"
            :class="[answerTone(index), { selected: isSelected(answer), locked: answerLocked }]"
            :aria-pressed="isMultipleQuestion ? (isSelected(answer) ? 'true' : 'false') : undefined"
            :disabled="answerLocked"
            @click="handleChoiceAnswer(answer)">
            <span class="answer-symbol"><v-icon>{{ answerIcon(index) }}</v-icon></span>
            <strong>{{ answer.text }}</strong>
            <span v-if="isSelected(answer)" class="answer-lock">
              <v-progress-circular v-if="answerPending && answerLocked" indeterminate size="20" width="2" />
              <v-icon v-else>{{ answerLocked ? "mdi-lock-check" : "mdi-check-circle" }}</v-icon>
            </span>
          </button>
        </div>

        <div v-if="isMultipleQuestion && !answerLocked" class="multiple-submit-bar">
          <span><v-icon>mdi-checkbox-multiple-marked-outline</v-icon>Wähle alle passenden Antworten.</span>
          <v-btn color="#58d388" depressed large :disabled="selectedAnswerIds.length === 0" :loading="answerPending" @click="submitMultipleAnswers">
            Auswahl bestätigen
          </v-btn>
        </div>

        <div v-if="answerLocked" class="answer-confirmation">
          <v-icon>mdi-check-circle-outline</v-icon><span>{{ isEstimateQuestion ? "Schätzung gespeichert" : "Antwort gespeichert" }} – warte auf die Auflösung.</span>
        </div>
      </section>

      <section v-else-if="snapshot.state === 'reveal'" class="stage reveal-stage">
        <div v-if="answersAreVisible" class="result-burst" :class="personalResultClass">
          <span><v-icon>{{ personalResultIcon }}</v-icon></span>
          <div>
            <small>{{ personalResultEyebrow }}</small>
            <h1>{{ personalResultTitle }}</h1>
            <p v-if="snapshot.personal && snapshot.personal.points !== null">+{{ snapshot.personal.points }} Punkte</p>
          </div>
        </div>
        <div v-else class="result-burst result-delayed">
          <span><v-icon>mdi-comment-question-outline</v-icon></span>
          <div>
            <small>ANTWORT GESPEICHERT</small>
            <h1>Die Auflösung folgt später.</h1>
            <p>Die richtigen Antworten werden in der Abschlussbesprechung gezeigt.</p>
          </div>
        </div>

        <div class="reveal-question">{{ snapshot.question.text }}</div>
        <div v-if="isEstimateQuestion && answersAreVisible" class="estimate-reveal-card">
          <div class="estimate-target">
            <small>RICHTIGER WERT</small>
            <strong>{{ formatEstimate(snapshot.question.targetValue) }} <span>{{ snapshot.question.unit }}</span></strong>
          </div>
          <div class="estimate-personal-value">
            <small>DEINE SCHÄTZUNG</small>
            <strong v-if="snapshot.personal && snapshot.personal.estimatedValue != null">
              {{ formatEstimate(snapshot.personal.estimatedValue) }} <span>{{ snapshot.question.unit }}</span>
            </strong>
            <strong v-else>Keine Abgabe</strong>
            <p v-if="snapshot.personal && snapshot.personal.distance != null">
              Abweichung: {{ formatEstimate(snapshot.personal.distance) }} {{ snapshot.question.unit }}
              <template v-if="snapshot.personal.distancePercent != null"> · {{ formatEstimate(snapshot.personal.distancePercent) }} %</template>
            </p>
          </div>
        </div>
        <div v-else-if="isEstimateQuestion" class="estimate-reveal-card">
          <div class="estimate-personal-value">
            <small>DEINE SCHÄTZUNG</small>
            <strong v-if="snapshot.personal && snapshot.personal.estimatedValue != null">
              {{ formatEstimate(snapshot.personal.estimatedValue) }} <span>{{ snapshot.question.unit }}</span>
            </strong>
            <strong v-else>Keine Abgabe</strong>
          </div>
          <div class="estimate-delayed-target">
            <small>RICHTIGER WERT</small>
            <strong><v-icon>mdi-eye-off-outline</v-icon> Noch verborgen</strong>
            <p>Der Zielwert folgt in der gemeinsamen Besprechung.</p>
          </div>
        </div>
        <div v-else class="reveal-answers" :class="{ 'solutions-hidden': !answersAreVisible }">
          <div
            v-for="(answer, index) in snapshot.question.answers"
            :key="answer._id"
            class="reveal-answer"
            :class="[answerTone(index), { correct: answersAreVisible && isCorrectAnswer(answer), selected: isSelected(answer) }]">
            <span class="answer-symbol"><v-icon>{{ answerIcon(index) }}</v-icon></span>
            <div><strong>{{ answer.text }}</strong><small>{{ answer.responseCount || 0 }} Antworten</small></div>
            <v-icon v-if="answersAreVisible && isCorrectAnswer(answer)" class="correct-mark">mdi-check-circle</v-icon>
          </div>
        </div>
        <p class="host-wait">
          <v-icon>{{ answersAreVisible ? "mdi-motion-play-outline" : "mdi-eye-off-outline" }}</v-icon>
          {{ answersAreVisible ? "Gleich geht es mit der Rangliste weiter." : "Die Lösung bleibt bis zur Abschlussbesprechung verborgen." }}
        </p>
      </section>

      <section v-else-if="snapshot.state === 'leaderboard'" class="stage leaderboard-stage">
        <span class="stage-kicker">ZWISCHENSTAND</span>
        <h1>Rangliste</h1>
        <div class="leaderboard-list">
          <div v-for="entry in snapshot.leaderboard.slice(0, 5)" :key="entry.participantId" class="leaderboard-row" :class="`rank-${entry.rank}`">
            <span class="rank">{{ entry.rank }}</span>
            <span class="rank-avatar">{{ participantInitials(entry.displayName) }}</span>
            <strong>{{ entry.displayName }}</strong>
            <span class="score">{{ formatScore(entry.score) }}</span>
          </div>
        </div>
        <div v-if="snapshot.personal" class="personal-rank">
          <span>Dein Rang</span><strong>#{{ snapshot.personal.rank }}</strong><small>{{ formatScore(snapshot.personal.score) }} Punkte</small>
        </div>
        <p class="host-wait"><v-icon>mdi-timer-sand</v-icon>Warte auf die nächste Frage.</p>
      </section>

      <section v-else-if="snapshot.state === 'paused'" class="stage paused-stage">
        <span class="paused-orbit"><i></i><v-icon>mdi-pause</v-icon></span>
        <span class="stage-kicker">KURZE PAUSE</span>
        <h1>Das Quiz geht später weiter.</h1>
        <p>Deine Antworten und Punkte sind sicher gespeichert. Du kannst die App oder diese Seite schliessen und später wieder ins Live Quiz einsteigen.</p>
        <div class="paused-player-card">
          <div><span><v-icon>mdi-map-marker-path</v-icon></span><small>Aktueller Stand</small><strong>{{ pauseContextLabel }}</strong></div>
          <div><span><v-icon>mdi-star-four-points-outline</v-icon></span><small>Deine Punkte</small><strong>{{ formatScore(snapshot.personal && snapshot.personal.score) }}</strong></div>
          <div><span><v-icon>mdi-clock-outline</v-icon></span><small>Runde bleibt offen bis</small><strong>{{ pauseExpiryLabel }}</strong></div>
        </div>
        <div class="paused-live-status"><i></i><span>Du wirst automatisch synchronisiert, sobald die Leitung fortsetzt.</span></div>
      </section>

      <section v-else-if="snapshot.state === 'finished'" class="stage finished-stage">
        <span class="stage-kicker">QUIZ ABGESCHLOSSEN</span>
        <h1>Das Podest</h1>
        <div class="podium" :class="{ 'single-winner': snapshot.leaderboard.length === 1 }">
          <div v-if="snapshot.leaderboard[1]" class="podium-place place-two">
            <span class="podium-avatar">{{ participantInitials(snapshot.leaderboard[1].displayName) }}</span>
            <strong>{{ snapshot.leaderboard[1].displayName }}</strong><small>{{ formatScore(snapshot.leaderboard[1].score) }}</small><i>2</i>
          </div>
          <div v-if="snapshot.leaderboard[0]" class="podium-place place-one">
            <v-icon class="crown">mdi-crown</v-icon>
            <span class="podium-avatar">{{ participantInitials(snapshot.leaderboard[0].displayName) }}</span>
            <strong>{{ snapshot.leaderboard[0].displayName }}</strong><small>{{ formatScore(snapshot.leaderboard[0].score) }}</small><i>1</i>
          </div>
          <div v-if="snapshot.leaderboard[2]" class="podium-place place-three">
            <span class="podium-avatar">{{ participantInitials(snapshot.leaderboard[2].displayName) }}</span>
            <strong>{{ snapshot.leaderboard[2].displayName }}</strong><small>{{ formatScore(snapshot.leaderboard[2].score) }}</small><i>3</i>
          </div>
        </div>
        <div v-if="snapshot.personal" class="final-personal">
          <span>Dein Ergebnis</span><strong>Rang {{ snapshot.personal.rank }}</strong><small>{{ formatScore(snapshot.personal.score) }} Punkte · {{ snapshot.personal.correctCount }} richtig</small>
        </div>
        <v-btn color="#58d388" class="finish-button" x-large depressed @click="leaveQuiz">Zur Quiz-Übersicht</v-btn>
      </section>

      <section v-else-if="snapshot.state === 'expired'" class="center-state error-state">
        <span><v-icon>mdi-clock-alert-outline</v-icon></span>
        <h1>Quiz-Pause abgelaufen</h1>
        <p>Diese Runde wurde nach zwölf Stunden ohne Fortsetzung automatisch geschlossen.</p>
        <v-btn outlined color="#58d388" large @click="leaveQuiz">Zur Übersicht</v-btn>
      </section>

      <section v-else class="center-state error-state">
        <span><v-icon>mdi-cancel</v-icon></span>
        <h1>Quiz beendet</h1>
        <p>Der Leiter hat diese Quiz-Runde geschlossen.</p>
        <v-btn outlined color="#58d388" large @click="leaveQuiz">Zur Übersicht</v-btn>
      </section>
    </template>
  </main>
</template>

<script>
import { brandLogo } from "@/constants/brandAssets"
import { mapState } from "vuex"

export default {
  name: "LiveQuizPlayer",
  data() {
    return {
      brandLogo,
      now: Date.now(),
      clockOffset: 0,
      clockTimer: null,
      soundEnabled: localStorage.getItem("roody-live-quiz-sound") === "on",
      music: null,
      fadeFrame: null,
      musicQuestionId: null,
      musicStarting: false,
      musicAttempt: 0,
      estimateInput: "",
      estimateValidationError: "",
    }
  },
  computed: {
    ...mapState("liveQuizStore", ["snapshot", "connected", "joining", "answerPending", "selectedAnswerIds", "answerLocked", "error"]),
    isConnected() {
      return this.connected
    },
    countdownValue() {
      if (!this.snapshot || !this.snapshot.countdownEndsAt) return 1
      return Math.max(1, Math.ceil((new Date(this.snapshot.countdownEndsAt).getTime() - this.serverNow) / 1000))
    },
    serverNow() {
      return this.now + this.clockOffset
    },
    remainingMs() {
      if (!this.snapshot || !this.snapshot.questionEndsAt) return 0
      return Math.max(0, new Date(this.snapshot.questionEndsAt).getTime() - this.serverNow)
    },
    remainingSeconds() {
      return Math.max(0, Math.ceil(this.remainingMs / 1000))
    },
    questionProgress() {
      if (!this.snapshot || !this.snapshot.questionStartedAt || !this.snapshot.questionEndsAt) return 0
      const start = new Date(this.snapshot.questionStartedAt).getTime()
      const end = new Date(this.snapshot.questionEndsAt).getTime()
      if (end <= start) return 0
      return Math.min(100, Math.max(0, ((end - this.serverNow) / (end - start)) * 100))
    },
    personalName() {
      return (this.snapshot && this.snapshot.personal && this.snapshot.personal.displayName) || "Quiz-Spieler"
    },
    initials() {
      return this.participantInitials(this.personalName)
    },
    isMultipleQuestion() {
      return this.snapshot && this.snapshot.question && this.snapshot.question.type === "multiple"
    },
    isEstimateQuestion() {
      return this.snapshot && this.snapshot.question && this.snapshot.question.type === "estimate"
    },
    answersAreVisible() {
      return !this.snapshot || this.snapshot.showCorrectAnswersImmediately !== false
    },
    hasEstimateInput() {
      return String(this.estimateInput == null ? "" : this.estimateInput).trim().length > 0
    },
    personalResultClass() {
      if (!this.snapshot.personal || !this.snapshot.personal.answered) return "result-missed"
      if (Number(this.snapshot.personal.creditFraction || 0) > 0 && !this.snapshot.personal.correct) return "result-partial"
      return this.snapshot.personal.correct ? "result-correct" : "result-wrong"
    },
    personalResultIcon() {
      if (!this.snapshot.personal || !this.snapshot.personal.answered) return "mdi-timer-off-outline"
      if (Number(this.snapshot.personal.creditFraction || 0) > 0 && !this.snapshot.personal.correct) return "mdi-circle-half-full"
      return this.snapshot.personal.correct ? "mdi-check-bold" : "mdi-close-thick"
    },
    personalResultEyebrow() {
      if (!this.snapshot.personal || !this.snapshot.personal.answered) return "Zeit abgelaufen"
      if (Number(this.snapshot.personal.creditFraction || 0) > 0 && !this.snapshot.personal.correct) return "Teilpunkte gesichert"
      if (this.isEstimateQuestion) return this.snapshot.personal.correct ? "Starke Schätzung" : "Ausserhalb der Wertung"
      return this.snapshot.personal.correct ? "Richtige Antwort" : "Leider daneben"
    },
    personalResultTitle() {
      if (!this.snapshot.personal || !this.snapshot.personal.answered) return "Nächste Chance nutzen"
      if (Number(this.snapshot.personal.creditFraction || 0) > 0 && !this.snapshot.personal.correct) return "Fast vollständig!"
      return this.snapshot.personal.correct ? "Stark gemacht!" : "Dranbleiben!"
    },
    pauseContextLabel() {
      if (!this.snapshot) return "Gespeichert"
      if (this.snapshot.resumeState === "lobby") return "Noch vor dem Start"
      if (this.snapshot.resumeState === "leaderboard") return `Nach Frage ${this.snapshot.currentQuestionIndex + 1}`
      return `Frage ${this.snapshot.currentQuestionIndex + 1} beendet`
    },
    pauseExpiryLabel() {
      if (!this.snapshot || !this.snapshot.expiresAt) return "In zwölf Stunden"
      const expiresAt = new Date(this.snapshot.expiresAt)
      const today = new Date()
      const tomorrow = new Date(today)
      tomorrow.setDate(today.getDate() + 1)
      const sameDay = (first, second) =>
        first.getFullYear() === second.getFullYear() && first.getMonth() === second.getMonth() && first.getDate() === second.getDate()
      const time = new Intl.DateTimeFormat("de-CH", { hour: "2-digit", minute: "2-digit" }).format(expiresAt)
      if (sameDay(expiresAt, today)) return `Heute, ${time} Uhr`
      if (sameDay(expiresAt, tomorrow)) return `Morgen, ${time} Uhr`
      return new Intl.DateTimeFormat("de-CH", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }).format(expiresAt)
    },
    connectedParticipantCount() {
      if (!this.snapshot) return 0
      if (this.snapshot.connectedParticipantCount != null && Number.isFinite(Number(this.snapshot.connectedParticipantCount))) {
        return Number(this.snapshot.connectedParticipantCount)
      }
      return (this.snapshot.participants || []).filter((participant) => participant.connected).length
    },
  },
  watch: {
    snapshot: {
      immediate: true,
      handler(snapshot, previousSnapshot) {
        if (!snapshot) return
        const questionId = snapshot.question ? String(snapshot.question._id) : null
        const previousQuestionId = previousSnapshot && previousSnapshot.question ? String(previousSnapshot.question._id) : null
        if (questionId !== previousQuestionId) {
          this.estimateInput = ""
          this.estimateValidationError = ""
        }
        if (snapshot.serverNow) this.clockOffset = new Date(snapshot.serverNow).getTime() - Date.now()
        if (snapshot.state === "question" && this.soundEnabled && snapshot.musicEnabled) {
          if (this.musicQuestionId !== questionId || (this.music && this.music.paused)) {
            this.musicQuestionId = questionId
            this.startMusic()
          }
        } else {
          this.musicQuestionId = null
          this.stopMusic()
        }
      },
    },
  },
  mounted() {
    this.prepareMusic()
    this.clockTimer = setInterval(() => {
      this.now = Date.now()
    }, 100)
    this.joinSession()
  },
  beforeDestroy() {
    if (this.clockTimer) clearInterval(this.clockTimer)
    this.destroyMusic()
    this.$store.dispatch("liveQuizStore/leave")
  },
  methods: {
    async joinSession() {
      try {
        await this.$store.dispatch("liveQuizStore/join", {
          sessionId: this.$route.params.sessionId,
          code: this.$route.query.code || null,
          mode: "player",
        })
      } catch (error) {
        console.error("Live-Quiz Beitritt fehlgeschlagen:", error)
      }
    },
    async handleChoiceAnswer(answer) {
      if (this.isMultipleQuestion) {
        this.$store.dispatch("liveQuizStore/toggleAnswer", answer._id)
        return
      }
      try {
        await this.$store.dispatch("liveQuizStore/answer", { answerIds: [answer._id] })
      } catch (error) {
        console.error("Live-Quiz Antwort fehlgeschlagen:", error)
      }
    },
    async submitMultipleAnswers() {
      if (!this.selectedAnswerIds.length) return
      try {
        await this.$store.dispatch("liveQuizStore/answer", { answerIds: this.selectedAnswerIds })
      } catch (error) {
        console.error("Live-Quiz Mehrfachantwort fehlgeschlagen:", error)
      }
    },
    async submitEstimate() {
      if (this.answerLocked || this.answerPending) return
      const estimatedValue = this.parseEstimate(this.estimateInput)
      if (estimatedValue == null || Math.abs(estimatedValue) > 1000000000000000) {
        this.estimateValidationError = "Bitte einen gültigen Zahlenwert eingeben."
        return
      }
      this.estimateValidationError = ""
      try {
        await this.$store.dispatch("liveQuizStore/answer", { estimatedValue })
      } catch (error) {
        console.error("Live-Quiz Schätzung fehlgeschlagen:", error)
      }
    },
    async leaveQuiz() {
      await this.$store.dispatch("liveQuizStore/leave", { intentional: true })
      if (this.$route.name !== "liveQuizLobby") this.$router.push({ name: "liveQuizLobby" })
    },
    clearError() {
      this.$store.dispatch("liveQuizStore/clearError")
    },
    isSelected(answer) {
      return this.selectedAnswerIds.includes(String(answer._id))
    },
    isCorrectAnswer(answer) {
      if (!this.snapshot.question) return false
      const correctIds = this.snapshot.question.correctAnswerIds && this.snapshot.question.correctAnswerIds.length
        ? this.snapshot.question.correctAnswerIds
        : [this.snapshot.question.correctAnswerId]
      return correctIds.filter(Boolean).map(String).includes(String(answer._id))
    },
    answerTone(index) {
      return ["tone-coral", "tone-blue", "tone-amber", "tone-violet"][index % 4]
    },
    answerIcon(index) {
      return ["mdi-triangle", "mdi-rhombus", "mdi-circle", "mdi-square"][index % 4]
    },
    participantInitials(name) {
      return String(name || "?")
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join("") || "?"
    },
    formatCode(code) {
      const value = String(code || "")
      return value.length === 6 ? `${value.slice(0, 3)} ${value.slice(3)}` : value
    },
    formatScore(score) {
      return new Intl.NumberFormat("de-CH").format(Number(score || 0))
    },
    formatEstimate(value) {
      if (value == null || !Number.isFinite(Number(value))) return "–"
      return new Intl.NumberFormat("de-CH", { maximumFractionDigits: 4 }).format(Number(value))
    },
    parseEstimate(value) {
      let normalized = String(value == null ? "" : value)
        .trim()
        .replace(/[\s']/g, "")
      if (!normalized) return null
      if (normalized.includes(",") && normalized.includes(".")) {
        if (normalized.lastIndexOf(",") > normalized.lastIndexOf(".")) normalized = normalized.replace(/\./g, "").replace(",", ".")
        else normalized = normalized.replace(/,/g, "")
      } else normalized = normalized.replace(",", ".")
      const parsed = Number(normalized)
      return Number.isFinite(parsed) ? parsed : null
    },
    prepareMusic() {
      this.music = new Audio("/music/quizMusic.mp3")
      this.music.loop = true
      this.music.preload = "auto"
      this.music.volume = 0
      this.music.load()
    },
    toggleSound() {
      this.soundEnabled = !this.soundEnabled
      localStorage.setItem("roody-live-quiz-sound", this.soundEnabled ? "on" : "off")
      if (this.soundEnabled && this.snapshot && this.snapshot.state === "question" && this.snapshot.musicEnabled) {
        this.musicQuestionId = this.snapshot.question ? String(this.snapshot.question._id) : null
        this.startMusic()
      } else if (this.soundEnabled) {
        this.unlockAudio()
      } else {
        this.musicQuestionId = null
        this.stopMusic()
      }
    },
    async unlockAudio() {
      if (!this.music || !this.soundEnabled) return
      const music = this.music
      try {
        music.volume = 0
        await music.play()
        music.pause()
      } catch (error) {
        this.soundEnabled = false
        localStorage.setItem("roody-live-quiz-sound", "off")
      }
    },
    startMusic() {
      if (!this.music || this.musicStarting) return
      const attempt = ++this.musicAttempt
      this.musicStarting = true
      const syncPlayback = () => {
        if (attempt !== this.musicAttempt) return
        if (this.snapshot && this.snapshot.questionStartedAt && Number.isFinite(this.music.duration) && this.music.duration > 0) {
          const elapsedSeconds = Math.max(0, (this.serverNow - new Date(this.snapshot.questionStartedAt).getTime()) / 1000)
          this.music.currentTime = elapsedSeconds % this.music.duration
        }
      }
      if (this.music.readyState >= 1) syncPlayback()
      else this.music.addEventListener("loadedmetadata", syncPlayback, { once: true })
      this.music.play().then(() => {
        if (
          attempt !== this.musicAttempt ||
          !this.snapshot ||
          this.snapshot.state !== "question" ||
          !this.soundEnabled ||
          !this.snapshot.musicEnabled
        ) {
          this.musicStarting = false
          if (this.music) {
            this.music.pause()
            this.music.volume = 0
          }
          return
        }
        this.musicStarting = false
        this.fadeMusic(0.32, 450)
      }).catch(() => {
        if (attempt !== this.musicAttempt) return
        this.musicStarting = false
        this.soundEnabled = false
        localStorage.setItem("roody-live-quiz-sound", "off")
      })
    },
    stopMusic() {
      this.musicAttempt += 1
      this.musicStarting = false
      if (!this.music || this.music.paused) return
      this.fadeMusic(0, 320, true)
    },
    fadeMusic(target, duration, pauseAfter = false) {
      if (!this.music) return
      if (this.fadeFrame) cancelAnimationFrame(this.fadeFrame)
      const startedAt = performance.now()
      const startVolume = this.music.volume
      const update = (timestamp) => {
        const progress = Math.min(1, (timestamp - startedAt) / duration)
        this.music.volume = Math.min(1, Math.max(0, startVolume + (target - startVolume) * progress))
        if (progress < 1) this.fadeFrame = requestAnimationFrame(update)
        else if (pauseAfter) this.music.pause()
      }
      this.fadeFrame = requestAnimationFrame(update)
    },
    destroyMusic() {
      this.musicAttempt += 1
      this.musicStarting = false
      if (this.fadeFrame) cancelAnimationFrame(this.fadeFrame)
      if (!this.music) return
      this.music.pause()
      this.music.src = ""
      this.music = null
    },
  },
}
</script>

<style scoped>
.quiz-play-page {
  --green: #58d388;
  --border: rgba(255, 255, 255, 0.11);
  position: relative;
  min-height: 100vh;
  overflow: hidden;
  color: #f5f8f6;
  background: radial-gradient(circle at 50% -10%, rgba(0, 150, 64, 0.18), transparent 36%), #080b0a;
  font-size: 1rem;
}

.play-ambient { position: fixed; right: -14rem; bottom: -16rem; width: 34rem; height: 34rem; border-radius: 50%; background: rgba(88, 211, 136, 0.08); filter: blur(5rem); pointer-events: none; }
.play-header { position: relative; z-index: 4; display: grid; grid-template-columns: 1fr auto 1fr; align-items: center; min-height: 4.75rem; padding: 0.75rem 1.25rem; border-bottom: 1px solid rgba(255, 255, 255, 0.06); background: rgba(8, 11, 10, 0.72); backdrop-filter: blur(1.25rem); }
.icon-button { display: grid; width: 2.875rem; height: 2.875rem; place-items: center; border: 1px solid var(--border); border-radius: 0.9375rem; color: #e8eeea; background: rgba(255, 255, 255, 0.045); }
.icon-button .v-icon { color: currentColor; }
.play-brand { display: flex; align-items: center; gap: 0.6rem; }
.play-brand > img { display: block; width: 3rem; height: 2.25rem; object-fit: contain; }
.play-brand > div { display: flex; min-width: 0; flex-direction: column; }
.play-brand strong { font-size: 0.875rem; font-weight: 850; letter-spacing: 0.1em; }
.play-brand small { color: #86948c; font-size: 0.75rem; }
.header-actions { display: flex; align-items: center; justify-content: flex-end; gap: 0.5rem; }
.connection-pill { display: inline-flex; align-items: center; gap: 0.4rem; min-height: 2rem; padding: 0 0.65rem; border: 1px solid rgba(88, 211, 136, 0.18); border-radius: 2rem; color: var(--green); background: rgba(88, 211, 136, 0.07); font-size: 0.75rem; font-weight: 800; }
.connection-pill i { width: 0.45rem; height: 0.45rem; border-radius: 50%; background: currentColor; }
.connection-pill.offline { border-color: rgba(255, 193, 7, 0.18); color: #ffd166; background: rgba(255, 193, 7, 0.07); }

.live-error { position: fixed; z-index: 10; top: 5.25rem; left: 50%; display: flex; width: min(calc(100% - 2rem), 42rem); align-items: center; gap: 0.625rem; padding: 0.75rem 0.875rem; border: 1px solid rgba(255, 107, 122, 0.32); border-radius: 0.875rem; color: #ff9aa4; background: rgba(61, 18, 24, 0.96); box-shadow: 0 1rem 2.5rem rgba(0, 0, 0, 0.3); translate: -50% 0; font-size: 0.875rem; }
.live-error span { flex: 1; }
.live-error .v-icon { color: currentColor; }
.live-error button { color: currentColor; }

.stage,
.center-state { position: relative; z-index: 1; width: min(calc(100% - 2rem), 72rem); min-height: calc(100vh - 4.75rem); margin: 0 auto; padding: 2.5rem 0 4rem; }
.center-state { display: flex; align-items: center; justify-content: center; flex-direction: column; text-align: center; }
.center-state h1 { margin: 1.25rem 0 0.5rem; font-size: clamp(2rem, 6vw, 3.5rem); }
.center-state p { margin: 0; color: #98a69f; font-size: 1rem; }
.loading-orbit { position: relative; display: grid; width: 6rem; height: 6rem; place-items: center; border: 1px solid rgba(88, 211, 136, 0.24); border-radius: 50%; background: rgba(88, 211, 136, 0.08); }
.loading-orbit .v-icon { color: var(--green); font-size: 2.25rem; }
.loading-orbit i { position: absolute; inset: -0.35rem; border: 0.2rem solid transparent; border-top-color: var(--green); border-radius: 50%; animation: orbit 1s linear infinite; }
.error-state > span { display: grid; width: 5rem; height: 5rem; place-items: center; border-radius: 1.5rem; background: rgba(255, 107, 122, 0.09); }
.error-state > span .v-icon { color: #ff7c89; font-size: 2rem; }
.state-actions { display: flex; flex-wrap: wrap; justify-content: center; gap: 0.75rem; margin-top: 1.5rem; }

.stage-kicker { display: inline-flex; align-items: center; gap: 0.5rem; color: var(--green); font-size: 0.75rem; font-weight: 900; letter-spacing: 0.16em; }
.stage-kicker i { width: 0.5rem; height: 0.5rem; border-radius: 50%; background: var(--green); box-shadow: 0 0 0 0.35rem rgba(88, 211, 136, 0.1); }
.lobby-stage { display: flex; align-items: center; flex-direction: column; text-align: center; }
.lobby-stage h1 { max-width: 56rem; margin: 1.25rem 0 0; font-size: clamp(2.5rem, 8vw, 5rem); line-height: 1; letter-spacing: -0.05em; }
.waiting-copy { margin: 1rem 0 2rem; color: #9eaaa4; font-size: 1rem; }
.player-ticket { display: grid; grid-template-columns: auto minmax(0, 1fr) auto; align-items: center; gap: 0.875rem; width: min(100%, 32rem); padding: 0.875rem; border: 1px solid rgba(88, 211, 136, 0.22); border-radius: 1.25rem; background: rgba(88, 211, 136, 0.07); text-align: left; }
.avatar-mark { display: grid; width: 3rem; height: 3rem; place-items: center; border-radius: 1rem; color: #07110a; background: var(--green); font-weight: 950; }
.player-ticket > div:nth-child(2) { display: flex; min-width: 0; flex-direction: column; }
.player-ticket small { color: #8e9b94; font-size: 0.75rem; }
.player-ticket strong { overflow: hidden; font-size: 1rem; text-overflow: ellipsis; white-space: nowrap; }
.player-ticket > span { display: inline-flex; align-items: center; gap: 0.35rem; color: var(--green); font-size: 0.75rem; font-weight: 800; }
.player-ticket > span .v-icon { color: currentColor; font-size: 1rem; }
.lobby-facts { display: grid; width: min(100%, 42rem); grid-template-columns: repeat(3, 1fr); gap: 0.75rem; margin-top: 1.25rem; }
.lobby-facts div { display: flex; min-height: 5.5rem; align-items: center; justify-content: center; flex-direction: column; border: 1px solid var(--border); border-radius: 1rem; background: rgba(255, 255, 255, 0.035); }
.lobby-facts span { font-size: 1.375rem; font-weight: 950; }
.lobby-facts small { margin-top: 0.2rem; color: #89978f; font-size: 0.75rem; }
.participant-cloud { display: flex; max-width: 50rem; flex-wrap: wrap; justify-content: center; gap: 0.5rem; margin-top: 1.5rem; }
.participant-cloud > span { display: inline-flex; align-items: center; gap: 0.45rem; min-height: 2.375rem; padding: 0.25rem 0.75rem 0.25rem 0.3rem; border: 1px solid var(--border); border-radius: 2rem; color: #cbd4cf; background: rgba(255, 255, 255, 0.04); font-size: 0.75rem; }
.participant-cloud > span i { display: grid; width: 1.75rem; height: 1.75rem; place-items: center; border-radius: 50%; color: #07110a; background: #91dfaf; font-style: normal; font-weight: 900; }
.participant-cloud > span.offline { opacity: 0.48; }

.countdown-stage { display: flex; align-items: center; justify-content: center; flex-direction: column; text-align: center; }
.countdown-number { display: grid; width: clamp(9rem, 32vw, 15rem); height: clamp(9rem, 32vw, 15rem); place-items: center; margin: 1.5rem 0; border: 0.125rem solid rgba(88, 211, 136, 0.25); border-radius: 50%; color: var(--green); background: radial-gradient(circle, rgba(88, 211, 136, 0.16), rgba(88, 211, 136, 0.02)); box-shadow: 0 0 5rem rgba(88, 211, 136, 0.13); font-size: clamp(5rem, 18vw, 9rem); font-weight: 950; line-height: 1; animation: countdownPop 700ms ease both; }
.countdown-stage h1 { margin: 0; font-size: clamp(2rem, 6vw, 3.5rem); }
.countdown-stage p { margin: 0.5rem 0 0; color: #93a099; }

.question-stage { padding-top: 1.5rem; }
.question-meta { display: flex; align-items: center; justify-content: space-between; gap: 1rem; color: #94a199; font-size: 0.75rem; font-weight: 800; letter-spacing: 0.04em; }
.answer-counter { display: inline-flex; align-items: center; gap: 0.35rem; }
.answer-counter .v-icon { color: var(--green); font-size: 1rem; }
.timer-track { height: 0.375rem; margin-top: 0.75rem; overflow: hidden; border-radius: 1rem; background: rgba(255, 255, 255, 0.07); }
.timer-track i { display: block; height: 100%; border-radius: inherit; background: linear-gradient(90deg, #00a94f, #7cf0a9); transition: width 100ms linear; }
.question-heading { display: grid; grid-template-columns: minmax(0, 1fr) auto; align-items: center; gap: 1.5rem; min-height: 13rem; padding: 2rem 0; }
.question-heading h1 { margin: 0; font-size: clamp(2rem, 5vw, 4rem); line-height: 1.12; letter-spacing: -0.04em; }
.timer-bubble { display: grid; width: 6.5rem; height: 6.5rem; place-items: center; align-content: center; border-radius: 50%; background: radial-gradient(circle at center, #111713 62%, transparent 64%), conic-gradient(var(--green) var(--timer-progress), rgba(255, 255, 255, 0.08) 0); }
.timer-bubble span { font-size: 2rem; font-weight: 950; line-height: 1; }
.timer-bubble small { margin-top: 0.2rem; color: #87958d; font-size: 0.75rem; }
.answer-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0.75rem; }
.estimate-answer-card { display: grid; grid-template-columns: auto minmax(0, 1fr); align-items: center; gap: 0.875rem; padding: 1.125rem; border: 1px solid rgba(73, 163, 255, 0.28); border-radius: 1.25rem; background: linear-gradient(135deg, rgba(73, 163, 255, 0.1), transparent 58%), rgba(255, 255, 255, 0.035); }
.estimate-icon { display: grid; width: 3.5rem; height: 3.5rem; place-items: center; border-radius: 1rem; background: rgba(73, 163, 255, 0.14); }
.estimate-icon .v-icon { color: #71b6ff; }
.estimate-copy { display: flex; min-width: 0; flex-direction: column; }
.estimate-copy strong { font-size: 1rem; }
.estimate-copy small { margin-top: 0.2rem; color: #91a19a; font-size: 0.875rem; }
.estimate-input-row { display: grid; grid-column: 1 / -1; grid-template-columns: minmax(0, 1fr) auto; align-items: start; gap: 0.75rem; }
.estimate-input-row .v-btn { min-height: 3.5rem; border-radius: 0.875rem; color: #07100a; font-size: 0.875rem; font-weight: 900; letter-spacing: 0; text-transform: none; }
.answer-button { position: relative; display: grid; grid-template-columns: auto minmax(0, 1fr) auto; align-items: center; gap: 1rem; min-height: 7rem; padding: 1rem 1.125rem; border: 1px solid rgba(255, 255, 255, 0.16); border: 1px solid color-mix(in srgb, var(--answer-color), transparent 52%); border-radius: 1.25rem; color: #f8faf9; background: rgba(255, 255, 255, 0.055); background: color-mix(in srgb, var(--answer-color), #0b0f0d 84%); text-align: left; transition: border-color 160ms ease, background 160ms ease, translate 160ms ease, opacity 160ms ease; }
.answer-button:not(:disabled):hover { translate: 0 -0.125rem; border-color: var(--answer-color); background: color-mix(in srgb, var(--answer-color), #0b0f0d 76%); }
.answer-button.locked:not(.selected) { opacity: 0.42; }
.answer-button.selected { border-color: var(--answer-color); background: rgba(88, 211, 136, 0.15); background: color-mix(in srgb, var(--answer-color), #0b0f0d 68%); box-shadow: inset 0 0 0 0.125rem rgba(255, 255, 255, 0.25); box-shadow: inset 0 0 0 0.125rem color-mix(in srgb, var(--answer-color), transparent 35%); }
.tone-coral { --answer-color: #ff5f68; }
.tone-blue { --answer-color: #49a3ff; }
.tone-amber { --answer-color: #f7c84b; }
.tone-violet { --answer-color: #b779ff; }
.answer-symbol { display: grid; width: 3.25rem; height: 3.25rem; place-items: center; border-radius: 1rem; background: rgba(255, 255, 255, 0.09); background: color-mix(in srgb, var(--answer-color), transparent 78%); }
.answer-symbol .v-icon { color: var(--answer-color); }
.answer-button strong { font-size: 1.125rem; line-height: 1.4; }
.answer-lock { display: grid; width: 2rem; height: 2rem; place-items: center; color: var(--answer-color); }
.answer-lock .v-icon { color: currentColor; }
.multiple-submit-bar { display: flex; align-items: center; justify-content: space-between; gap: 1rem; margin-top: 0.875rem; padding: 0.75rem 0.875rem; border: 1px solid rgba(88, 211, 136, 0.2); border-radius: 1rem; background: rgba(88, 211, 136, 0.055); }
.multiple-submit-bar > span { display: inline-flex; align-items: center; gap: 0.45rem; color: #aab7b0; font-size: 0.875rem; }
.multiple-submit-bar > span .v-icon { color: var(--green); }
.multiple-submit-bar .v-btn { border-radius: 0.875rem; color: #07100a; font-size: 0.875rem; font-weight: 900; letter-spacing: 0; text-transform: none; }
.answer-confirmation { display: flex; align-items: center; justify-content: center; gap: 0.5rem; margin-top: 1rem; color: var(--green); font-size: 0.875rem; font-weight: 750; }
.answer-confirmation .v-icon { color: currentColor; }

.reveal-stage { max-width: 58rem; }
.result-burst { display: grid; grid-template-columns: auto minmax(0, 1fr); align-items: center; gap: 1rem; padding: 1.25rem; border: 1px solid var(--result-border); border-radius: 1.5rem; background: var(--result-background); }
.result-correct { --result-border: rgba(88, 211, 136, 0.32); --result-background: rgba(88, 211, 136, 0.09); --result-color: #58d388; }
.result-partial { --result-border: rgba(73, 163, 255, 0.34); --result-background: rgba(73, 163, 255, 0.09); --result-color: #71b6ff; }
.result-wrong { --result-border: rgba(255, 95, 104, 0.32); --result-background: rgba(255, 95, 104, 0.09); --result-color: #ff7b83; }
.result-missed { --result-border: rgba(255, 200, 75, 0.3); --result-background: rgba(255, 200, 75, 0.08); --result-color: #f7c84b; }
.result-delayed { --result-border: rgba(73, 163, 255, 0.3); --result-background: rgba(73, 163, 255, 0.08); --result-color: #71b6ff; }
.result-burst > span { display: grid; width: 4.5rem; height: 4.5rem; place-items: center; border-radius: 1.25rem; color: var(--result-color); background: rgba(255, 255, 255, 0.08); background: color-mix(in srgb, var(--result-color), transparent 84%); }
.result-burst > span .v-icon { color: currentColor; font-size: 2rem; }
.result-burst small { color: var(--result-color); font-size: 0.75rem; font-weight: 850; letter-spacing: 0.08em; text-transform: uppercase; }
.result-burst h1 { margin: 0.15rem 0 0; font-size: clamp(1.75rem, 5vw, 3rem); }
.result-burst p { margin: 0.25rem 0 0; color: #d5ded9; font-size: 1rem; font-weight: 800; }
.reveal-question { margin: 1.5rem 0 0.875rem; color: #cbd5d0; font-size: 1.125rem; font-weight: 800; }
.estimate-reveal-card { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0.75rem; }
.estimate-target,
.estimate-personal-value,
.estimate-delayed-target { display: flex; min-height: 8rem; justify-content: center; flex-direction: column; padding: 1rem; border: 1px solid var(--border); border-radius: 1.125rem; background: rgba(255, 255, 255, 0.035); }
.estimate-target { border-color: rgba(88, 211, 136, 0.28); background: rgba(88, 211, 136, 0.07); }
.estimate-target small,
.estimate-personal-value small,
.estimate-delayed-target small { color: #87958d; font-size: 0.75rem; font-weight: 850; letter-spacing: 0.08em; }
.estimate-target strong,
.estimate-personal-value strong,
.estimate-delayed-target strong { margin-top: 0.35rem; font-size: 1.75rem; }
.estimate-target strong span,
.estimate-personal-value strong span { color: #9eaaa3; font-size: 1rem; }
.estimate-personal-value p { margin: 0.35rem 0 0; color: #99a69f; font-size: 0.875rem; }
.estimate-delayed-target { border-color: rgba(73, 163, 255, 0.22); background: rgba(73, 163, 255, 0.055); }
.estimate-delayed-target strong { display: inline-flex; align-items: center; gap: 0.5rem; color: #dce5e0; font-size: 1.25rem; }
.estimate-delayed-target strong .v-icon { color: #71b6ff; }
.estimate-delayed-target p { margin: 0.35rem 0 0; color: #99a69f; font-size: 0.875rem; }
.reveal-answers { display: grid; gap: 0.625rem; grid-template-columns: repeat(2, minmax(0, 1fr)); }
.reveal-answer { display: grid; grid-template-columns: auto minmax(0, 1fr) auto; align-items: center; gap: 0.75rem; min-height: 5rem; padding: 0.75rem; border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 1rem; background: rgba(255, 255, 255, 0.03); opacity: 0.58; }
.reveal-answers.solutions-hidden .reveal-answer { opacity: 1; }
.reveal-answer.correct { border-color: rgba(88, 211, 136, 0.5); border-color: color-mix(in srgb, var(--answer-color), transparent 42%); background: rgba(88, 211, 136, 0.09); background: color-mix(in srgb, var(--answer-color), #0b0f0d 88%); opacity: 1; }
.reveal-answer.selected { box-shadow: inset 0 0 0 0.125rem rgba(255, 255, 255, 0.12); }
.reveal-answer .answer-symbol { width: 2.75rem; height: 2.75rem; }
.reveal-answer > div { display: flex; min-width: 0; flex-direction: column; }
.reveal-answer strong { font-size: 1rem; }
.reveal-answer small { color: #8f9d95; font-size: 0.75rem; }
.correct-mark { color: var(--answer-color) !important; }
.host-wait { display: flex; align-items: center; justify-content: center; gap: 0.5rem; margin: 1.5rem 0 0; color: #8f9c95; font-size: 0.875rem; }
.host-wait .v-icon { color: var(--green); }

.leaderboard-stage { max-width: 50rem; text-align: center; }
.leaderboard-stage h1,
.finished-stage h1 { margin: 0.5rem 0 1.5rem; font-size: clamp(2.5rem, 7vw, 4.5rem); letter-spacing: -0.05em; }
.leaderboard-list { display: grid; gap: 0.5rem; text-align: left; }
.leaderboard-row { display: grid; grid-template-columns: 2.5rem 2.75rem minmax(0, 1fr) auto; align-items: center; gap: 0.75rem; min-height: 4.5rem; padding: 0.625rem 1rem; border: 1px solid var(--border); border-radius: 1rem; background: rgba(255, 255, 255, 0.035); }
.leaderboard-row .rank { color: #7f8e86; font-size: 1.125rem; font-weight: 950; }
.rank-avatar { display: grid; width: 2.5rem; height: 2.5rem; place-items: center; border-radius: 0.875rem; color: #07100a; background: #8aa198; font-size: 0.75rem; font-weight: 900; }
.leaderboard-row strong { overflow: hidden; font-size: 1rem; text-overflow: ellipsis; white-space: nowrap; }
.leaderboard-row .score { color: var(--green); font-size: 1rem; font-weight: 900; }
.rank-1 { border-color: rgba(247, 200, 75, 0.35); background: rgba(247, 200, 75, 0.07); }
.rank-1 .rank-avatar { background: #f7c84b; }
.rank-2 .rank-avatar { background: #c9d0cc; }
.rank-3 .rank-avatar { background: #d28f61; }
.personal-rank { display: grid; grid-template-columns: minmax(0, 1fr) auto; align-items: center; margin-top: 1rem; padding: 1rem 1.25rem; border: 1px solid rgba(88, 211, 136, 0.28); border-radius: 1rem; background: rgba(88, 211, 136, 0.07); text-align: left; }
.personal-rank span { color: #a9b5ae; font-size: 0.875rem; }
.personal-rank strong { grid-row: 1 / span 2; grid-column: 2; color: var(--green); font-size: 2rem; }
.personal-rank small { color: #d2dbd6; font-size: 0.75rem; }

.paused-stage { display: flex; max-width: 46rem; align-items: center; flex-direction: column; text-align: center; }
.paused-orbit { position: relative; display: grid; width: 5.5rem; height: 5.5rem; place-items: center; margin-bottom: 1.25rem; border-radius: 1.75rem; color: var(--green); background: rgba(88, 211, 136, 0.11); }
.paused-orbit i { position: absolute; inset: -0.375rem; border: 1px solid rgba(88, 211, 136, 0.28); border-right-color: transparent; border-radius: 1.95rem; animation: orbit 6s linear infinite; }
.paused-orbit .v-icon { color: currentColor; font-size: 2.75rem; }
.paused-stage > h1 { margin: 0.625rem 0 0; font-size: clamp(2.5rem, 7vw, 4rem); line-height: 1.02; letter-spacing: -0.05em; }
.paused-stage > p { max-width: 39rem; margin: 1rem auto 0; color: #9ba8a1; font-size: 1rem; line-height: 1.6; }
.paused-player-card { display: grid; width: 100%; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 0.625rem; margin-top: 1.75rem; text-align: left; }
.paused-player-card > div { display: grid; grid-template-columns: auto minmax(0, 1fr); grid-template-rows: auto auto; column-gap: 0.625rem; padding: 0.875rem; border: 1px solid var(--border); border-radius: 1rem; background: rgba(255, 255, 255, 0.035); }
.paused-player-card > div > span { display: grid; width: 2.5rem; height: 2.5rem; grid-row: 1 / span 2; place-items: center; border-radius: 0.75rem; color: var(--green); background: rgba(88, 211, 136, 0.09); }
.paused-player-card .v-icon { color: currentColor; font-size: 1.25rem; }
.paused-player-card small { color: #839188; font-size: 0.75rem; }
.paused-player-card strong { margin-top: 0.125rem; overflow-wrap: anywhere; font-size: 0.875rem; }
.paused-live-status { display: inline-flex; align-items: center; gap: 0.5rem; margin-top: 1.25rem; padding: 0.625rem 0.875rem; border-radius: 2rem; color: #aebbb4; background: rgba(255, 255, 255, 0.04); font-size: 0.875rem; }
.paused-live-status i { width: 0.5rem; height: 0.5rem; flex: 0 0 auto; border-radius: 50%; background: var(--green); box-shadow: 0 0 0 0.3rem rgba(88, 211, 136, 0.09); }

.finished-stage { display: flex; max-width: 58rem; align-items: center; flex-direction: column; text-align: center; }
.podium { display: flex; width: 100%; min-height: 22rem; align-items: flex-end; justify-content: center; gap: 0.625rem; margin-top: 1rem; }
.podium-place { position: relative; display: flex; width: min(31%, 14rem); align-items: center; flex-direction: column; padding: 1rem 0.5rem 0; border: 1px solid var(--podium-border); border-bottom: 0; border-radius: 1.25rem 1.25rem 0 0; background: var(--podium-bg); }
.place-one { --podium-border: rgba(247, 200, 75, 0.5); --podium-bg: linear-gradient(180deg, rgba(247, 200, 75, 0.16), rgba(247, 200, 75, 0.04)); min-height: 20rem; }
.place-two { --podium-border: rgba(205, 216, 210, 0.35); --podium-bg: linear-gradient(180deg, rgba(205, 216, 210, 0.12), rgba(205, 216, 210, 0.03)); min-height: 15rem; }
.place-three { --podium-border: rgba(210, 143, 97, 0.4); --podium-bg: linear-gradient(180deg, rgba(210, 143, 97, 0.13), rgba(210, 143, 97, 0.03)); min-height: 12rem; }
.podium-avatar { display: grid; width: 4rem; height: 4rem; place-items: center; border-radius: 1.25rem; color: #08100b; background: #dae3de; font-size: 1.125rem; font-weight: 950; }
.place-one .podium-avatar { background: #f7c84b; }
.place-three .podium-avatar { background: #d28f61; }
.podium-place strong { width: 100%; margin-top: 0.75rem; overflow: hidden; font-size: 0.875rem; text-overflow: ellipsis; white-space: nowrap; }
.podium-place small { margin-top: 0.25rem; color: #aeb9b3; font-size: 0.75rem; }
.podium-place > i { margin-top: auto; padding-bottom: 0.75rem; color: rgba(255, 255, 255, 0.2); font-size: 3rem; font-style: normal; font-weight: 950; }
.crown { position: absolute; top: -2.5rem; color: #f7c84b !important; font-size: 2.25rem !important; }
.single-winner .place-one { width: min(70%, 16rem); }
.final-personal { display: flex; width: min(100%, 32rem); align-items: center; flex-direction: column; margin-top: 1.5rem; padding: 1rem; border: 1px solid rgba(88, 211, 136, 0.28); border-radius: 1rem; background: rgba(88, 211, 136, 0.07); }
.final-personal span { color: #92a098; font-size: 0.75rem; }
.final-personal strong { color: var(--green); font-size: 1.5rem; }
.final-personal small { color: #cbd5d0; font-size: 0.875rem; }
.finish-button { min-width: 15rem !important; margin-top: 1.25rem; border-radius: 1rem !important; color: #07100a !important; font-size: 1rem !important; font-weight: 900 !important; text-transform: none !important; }

@keyframes orbit { to { rotate: 360deg; } }
@keyframes countdownPop { 0% { opacity: 0; } 35% { opacity: 1; } 100% { opacity: 1; } }

@media (max-width: 47.9375rem) {
  .play-header { grid-template-columns: auto 1fr auto; min-height: 4.25rem; padding: 0.625rem 0.75rem; }
  .play-brand { justify-self: center; gap: 0.45rem; }
  .play-brand > img { width: 2.5rem; height: 1.875rem; }
  .play-brand strong { font-size: 0.75rem; }
  .play-brand small { display: none; }
  .connection-pill { display: none; }
  .stage,
  .center-state { width: min(calc(100% - 1.25rem), 42rem); min-height: calc(100vh - 4.25rem); padding: 1.5rem 0 2.5rem; }
  .lobby-stage h1 { font-size: clamp(2.5rem, 13vw, 4rem); }
  .lobby-facts { gap: 0.4rem; }
  .lobby-facts div { min-height: 5rem; }
  .lobby-facts span { font-size: 1.125rem; }
  .question-heading { min-height: 10rem; gap: 0.75rem; padding: 1.25rem 0; }
  .question-heading h1 { font-size: clamp(1.75rem, 8vw, 2.75rem); }
  .timer-bubble { width: 4.75rem; height: 4.75rem; }
  .timer-bubble span { font-size: 1.5rem; }
  .answer-grid,
  .reveal-answers,
  .estimate-reveal-card { grid-template-columns: 1fr; }
  .estimate-input-row { grid-template-columns: 1fr; }
  .estimate-input-row .v-btn { width: 100%; }
  .multiple-submit-bar { align-items: stretch; flex-direction: column; }
  .multiple-submit-bar .v-btn { width: 100%; }
  .answer-button { min-height: 5.75rem; }
  .answer-symbol { width: 2.875rem; height: 2.875rem; }
  .result-burst { padding: 1rem; }
  .result-burst > span { width: 3.75rem; height: 3.75rem; }
  .leaderboard-row { grid-template-columns: 2rem 2.5rem minmax(0, 1fr) auto; gap: 0.5rem; padding-inline: 0.75rem; }
  .paused-stage { align-items: stretch; text-align: left; }
  .paused-orbit { align-self: center; width: 4.5rem; height: 4.5rem; border-radius: 1.375rem; }
  .paused-orbit .v-icon { font-size: 2.25rem; }
  .paused-stage > h1 { font-size: clamp(2.25rem, 11vw, 3.25rem); }
  .paused-stage > p { margin-inline: 0; font-size: 0.875rem; }
  .paused-player-card { grid-template-columns: 1fr; margin-top: 1.25rem; }
  .paused-live-status { align-items: flex-start; border-radius: 1rem; line-height: 1.45; }
  .paused-live-status i { margin-top: 0.35rem; }
  .podium { min-height: 18rem; gap: 0.3rem; }
  .podium-place { width: 32%; padding-inline: 0.25rem; }
  .place-one { min-height: 17rem; }
  .place-two { min-height: 13rem; }
  .place-three { min-height: 10rem; }
  .podium-avatar { width: 3rem; height: 3rem; border-radius: 1rem; font-size: 0.875rem; }
  .podium-place strong { font-size: 0.75rem; }
}

@media (prefers-reduced-motion: reduce) {
  .loading-orbit i,
  .paused-orbit i,
  .countdown-number { animation: none; }
  .answer-button { transition: none; }
}
</style>
